/**
 * NavBar — 챗봇 위젯 하단 네비게이션
 *
 * 사용자가 버튼식 응답 후에도 언제든 다른 동선으로 이동할 수 있도록
 * 입력창 위에 항시 노출되는 4-탭 바.
 *
 * - 홈            : 대화 초기화 → 인사 메시지 재출력
 * - 서비스         : 기능 탐색 동선 (7개 카테고리 그리드)
 * - 자주 찾는 질문 : 외부 FAQ 페이지(maillink.co.kr/intro/faq)로 새 탭 이동
 * - 상담원         : 운영시간 분기 (1:1 상담 / 전화)
 */

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

const FAQ_EXTERNAL_URL = 'https://www.maillink.co.kr/intro/faq';

interface NavBarProps {
  onHome: () => void;
  onShowServices: () => void;
  onContactAgent: () => void;
}

export function NavBar({
  onHome,
  onShowServices,
  onContactAgent,
}: NavBarProps) {
  const config = useAuthStore((s) => s.config);
  const isOperatingNow = config?.is_operating_now ?? false;

  function handleOpenFAQ() {
    window.open(FAQ_EXTERNAL_URL, '_blank', 'noopener,noreferrer');
  }

  return (
    <nav
      className={cn(
        'ml-flex ml-items-stretch',
        'ml-bg-white ml-border-t ml-border-line',
        'ml-flex-shrink-0'
      )}
      aria-label="챗봇 네비게이션"
    >
      <NavItem icon={<HomeIcon />} label="홈" onClick={onHome} primary />
      <NavItem icon={<GridIcon />} label="서비스" onClick={onShowServices} />
      <NavItem icon={<ListIcon />} label="자주 찾는 질문" onClick={handleOpenFAQ} />
      <NavItem
        icon={<HeadsetIcon />}
        label="상담원"
        onClick={onContactAgent}
        badge={isOperatingNow ? 'online' : 'offline'}
      />
    </nav>
  );
}

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  primary?: boolean;
  badge?: 'online' | 'offline';
}

function NavItem({ icon, label, onClick, primary = false, badge }: NavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'ml-flex-1 ml-flex ml-flex-col ml-items-center ml-justify-center',
        'ml-py-2 ml-px-1 ml-gap-0.5',
        'ml-text-mute ml-cursor-pointer ml-relative',
        'ml-transition-colors ml-duration-150',
        'hover:ml-bg-surface hover:ml-text-brand-600',
        primary && 'ml-text-brand-600',
        // 모바일: 패딩 살짝 축소
        'max-[480px]:ml-py-1.5'
      )}
      aria-label={label}
    >
      <span className="ml-relative">
        {icon}
        {badge === 'online' && (
          <span
            className="ml-absolute ml--top-0.5 ml--right-0.5 ml-w-1.5 ml-h-1.5 ml-rounded-full ml-bg-ok"
            aria-label="운영 중"
          />
        )}
        {badge === 'offline' && (
          <span
            className="ml-absolute ml--top-0.5 ml--right-0.5 ml-w-1.5 ml-h-1.5 ml-rounded-full ml-bg-mute"
            aria-label="운영 시간 외"
          />
        )}
      </span>
      <span className="ml-text-[10px] ml-font-medium ml-leading-tight">{label}</span>
    </button>
  );
}

function HomeIcon() {
  return (
    <svg
      className="ml-w-[18px] ml-h-[18px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      className="ml-w-[18px] ml-h-[18px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg
      className="ml-w-[18px] ml-h-[18px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
      />
    </svg>
  );
}

function HeadsetIcon() {
  return (
    <svg
      className="ml-w-[18px] ml-h-[18px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18.364 5.636A9 9 0 105.636 18.364 9 9 0 0018.364 5.636zM12 14a2 2 0 100-4 2 2 0 000 4z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 12a7 7 0 0114 0M3 12v3a2 2 0 002 2h1v-7H5a2 2 0 00-2 2zm18 0v3a2 2 0 01-2 2h-1v-7h1a2 2 0 012 2z"
      />
    </svg>
  );
}
